import {ClientConfigurationService} from "../../services/clientConfigurationService";
import {ApplicationInsightsTelemetry} from "../telemetry/applicationInsightsTelemetry";

/**
 * Configuration of the app.
 */
export interface AppConfiguration {
  /**
   * The connection string for Application Insights.
   */
  applicationInsightsConnectionString: string;

  /**
   * The app's role name on Application Insights.
   */
  applicationInsightsCloudRoleName: string;

  /**
   * The URI to download the printable calendar.
   */
  printableCalendarUri: string;

  /**
   * The URI to download the featured events.
   */
  printableFeaturedEventsUri: string;

  /**
   * Feature toggles for Admin Portal.
   */
  featureToggles: string[];
}

/**
 * Contain methods to initialize the app.
 */
export class AppInitializer {
  private static _appInitializerInstance: AppInitializer;
  private readonly _clientConfigService: ClientConfigurationService;
  private readonly _cloudRoleName = "AdminPortalClient";
  private _appConfig: AppConfiguration | undefined;

  private constructor() {
    this._clientConfigService = new ClientConfigurationService();
  }

  /**
   * The configuration of Admin Portal.
   */
  public get AppConfig(): AppConfiguration | undefined {
    return this._appConfig;
  }

  /**
   * Get the instance of AppConfiguration.
   */
  public static getInstance(): AppInitializer {
    if (!AppInitializer._appInitializerInstance) {
      this._appInitializerInstance = new AppInitializer();
    }

    return this._appInitializerInstance;
  }

  /**
   * Get the application configuration and initialize the app with the configuration.
   */
  public async initializeApp(): Promise<void> {
    this._appConfig = await this.getAppConfiguration();

    // Initialize Application Insights.
    ApplicationInsightsTelemetry.getInstance().initializeApplicationInsights(
      this._appConfig
    );
  }

  /**
   * Get the configuration of the app from the client configuration endpoint.
   */
  public async getAppConfiguration(): Promise<AppConfiguration> {
    let data = await this._clientConfigService.getClientConfiguration();
    return {
      applicationInsightsConnectionString:
        data.applicationInsightsConnectionString,
      applicationInsightsCloudRoleName: this._cloudRoleName,
      printableCalendarUri: data.printableCalendarUri,
      printableFeaturedEventsUri: data.printableFeaturedEventsUri,
      featureToggles: data.featureToggles
    };
  }
}
